import request from 'api/requestUtils'
import { templates } from '@luxuryescapes/lib-uri-templates'
import createBatchedPromise from './createBatchedPromise'
import type { paths } from '@luxuryescapes/contract-svc-reservation/index'

export function createSurchargeMarginKey(ratePlanId: string, startDate: string, endDate: string) {
  return `${ratePlanId}_${startDate}_${endDate}`
}

type SurchargeMarginBatchCalculateServerResponse = {
  status: 200 | 201 | 204
  message?: string
  result: Array<{
    /** Format: uuid */
    roomRateId: string
    /** Format: ISO8601 */
    checkIn: string
    /** Format: ISO8601 */
    checkOut: string
    marginAud: number
  }>
  count?: number
}
type SurchargeMarginBatchCalculateServerRequestPayload = paths['/api/rate-plans/surcharge-dates/margin']['post']['requestBody']['content']['application/json']

export interface LereSurchargeTokenPayload {
  offerId: string
  roomTypeId: string
  roomRateId: string
  checkIn: string
  checkOut: string
  lereGroup: string
}

type ContractLereSurchargeTokenBatchRequest = paths['/api/properties/lere/surcharge-token']['post']['requestBody']['content']['application/json']
type ContractLereSurchargeTokenBatchResponse = paths['/api/properties/lere/surcharge-token']['post']['responses']['200']['content']['application/json']
type LereSurchargeTokenBatchRequest = Omit<ContractLereSurchargeTokenBatchRequest, 'items'> & {
  items: Array<ContractLereSurchargeTokenBatchRequest['items'][number] & { roomRateId: string }>
}
type ContractLereSurchargeTokenBatchResponseToken = Exclude<ContractLereSurchargeTokenBatchResponse['result']['tokens'][number], null>
type LereSurchargeTokenBatchResponseToken = ContractLereSurchargeTokenBatchResponseToken & { roomRateId: string }
type LereSurchargeTokenBatchResponse = Omit<ContractLereSurchargeTokenBatchResponse, 'result'> & {
  result: Omit<ContractLereSurchargeTokenBatchResponse['result'], 'tokens'> & {
    tokens: Array<LereSurchargeTokenBatchResponseToken | null>
  }
}

export const LERE_SURCHARGE_TOKEN_TIMEOUT_MS = 2000

export function createLereSurchargeTokenKey(payload: LereSurchargeTokenPayload) {
  return [
    payload.offerId,
    payload.roomTypeId,
    payload.roomRateId,
    payload.checkIn,
    payload.checkOut,
    payload.lereGroup,
  ].join('_')
}

function createLereSurchargeTokenItemKey(
  item: Pick<LereSurchargeTokenPayload, 'offerId' | 'roomTypeId' | 'roomRateId' | 'checkIn' | 'checkOut'>,
) {
  return [
    item.offerId,
    item.roomTypeId,
    item.roomRateId,
    item.checkIn,
    item.checkOut,
  ].join('_')
}

interface LereSurchargeTokenOptions {
  timeout?: number
  accessToken?: string
}

export async function createLereSurchargeTokens(
  payloads: Array<LereSurchargeTokenPayload>,
  currency: string,
  distinctId?: string,
  options?: LereSurchargeTokenOptions,
): Promise<Record<string, App.LereSurchargeToken>> {
  if (payloads.length === 0) {
    return {}
  }

  const lereGroup = payloads[0].lereGroup
  const timeout = options?.timeout ?? LERE_SURCHARGE_TOKEN_TIMEOUT_MS
  const response = await request.post<LereSurchargeTokenBatchResponse, LereSurchargeTokenBatchRequest>(
    '/api/properties/lere/surcharge-token',
    {
      lereGroup,
      currency,
      distinctId,
      items: payloads.map(({ offerId, roomTypeId, roomRateId, checkIn, checkOut }) => ({
        offerId,
        roomTypeId,
        roomRateId,
        checkIn,
        checkOut,
      })),
    },
    {
      credentials: 'include',
      excludeBrand: true,
      timeout,
      headers: options?.accessToken ? { Authorization: `Bearer ${options.accessToken}` } : undefined,
    },
  )

  const tokensByItemKey = new Map<string, LereSurchargeTokenBatchResponseToken>()
  for (const token of response.result.tokens ?? []) {
    if (token) {
      tokensByItemKey.set(createLereSurchargeTokenItemKey(token), token)
    }
  }

  return payloads.reduce<Record<string, App.LereSurchargeToken>>((tokensByKey, payload) => {
    const key = createLereSurchargeTokenKey(payload)
    const matchedToken = tokensByItemKey.get(createLereSurchargeTokenItemKey(payload))

    tokensByKey[key] = matchedToken?.token ? {
      status: 'success',
      surchargeAmount: matchedToken.surcharge_amount ?? 0,
      token: matchedToken.token,
    } : {
      status: 'failed',
    }

    return tokensByKey
  }, {})
}

export const batchFetchSurchargeMargin = createBatchedPromise({
  argsReducerFn: (
    accumulatedArgs: Record<string, { startDate: string, endDate: string }>,
    singleArg: [roomRateId: string, surchargeDateBlockInfo: { startDate: string, endDate: string }],
  ) => {
    const [ratePlanId, surchargeDateBlockInfo] = singleArg
    return {
      ...accumulatedArgs,
      [ratePlanId]: { ...surchargeDateBlockInfo },
    }
  },
  batchRequestFn: (batchedArgs) => {
    return requestPostBatchSurchargeMargin(batchedArgs)
  },
  singleValueFn: (singleArg, batchedResponse): App.SurchargeMargin => {
    const roomRateId = singleArg[0]
    const checkIn = singleArg[1].startDate
    const checkOut = singleArg[1].endDate
    const singleResponse = batchedResponse.find((response) => response.roomRateId === roomRateId && response.checkIn === checkIn && response.checkOut === checkOut)
    return {
      status: 'success',
      roomRateId,
      checkIn,
      checkOut,
      marginAud: singleResponse?.marginAud ?? 0,
    }
  },
})

async function requestPostBatchSurchargeMargin(
  payload: Record<string, { startDate: string, endDate: string }>,
) {
  try {
    const response = await request.post<SurchargeMarginBatchCalculateServerResponse, SurchargeMarginBatchCalculateServerRequestPayload>(
      '/api/rate-plans/surcharge-dates/margin',
      payload,
      { credentials: 'include' },
    )

    return response.result
  } catch(e) {
    return Promise.reject(e)
  }
}

export type ExtraGuestSurchargeQuote =
  paths['/api/properties/{propertyId}/room-types/{roomTypeId}/room-rates/{roomRateId}/extra-guest-surcharges/quote']['get']['responses']['200']['content']['application/json']['result']

/** Date-free upfront extra-guest-surcharge quote for a room rate (BNBL v2). */
export function quoteExtraGuestSurcharge(
  propertyId: string,
  roomTypeId: string,
  roomRateId: string,
  params: {
    currency: string
    nights: number
    numberOfAdults: number
    numberOfChildren?: number
    numberOfInfants?: number
    numberOfTeenagers?: number
    offerType?: string
  },
): Promise<ExtraGuestSurchargeQuote> {
  const query = new URLSearchParams({
    currency: params.currency,
    nights: String(params.nights),
    number_of_adults: String(params.numberOfAdults),
    number_of_children: String(params.numberOfChildren ?? 0),
    number_of_infants: String(params.numberOfInfants ?? 0),
    number_of_teenagers: String(params.numberOfTeenagers ?? 0),
  })
  if (params.offerType) {
    query.set('offer_type', params.offerType)
  }
  return request
    .get<App.ApiResponse<ExtraGuestSurchargeQuote>>(
      `/api/properties/${propertyId}/room-types/${roomTypeId}/room-rates/${roomRateId}/extra-guest-surcharges/quote?${query.toString()}`,
      { credentials: 'include' },
    )
    .then(data => data.result)
}

export type ExtraGuestSurchargePatternsQuote =
  paths['/api/properties/{propertyId}/room-types/{roomTypeId}/room-rates/{roomRateId}/extra-guest-surcharges/quote-patterns']['get']['responses']['200']['content']['application/json']['result']

export type ExtraGuestSurchargePattern = ExtraGuestSurchargePatternsQuote['patterns'][number]

/** Prices every bookable occupancy pattern for a room rate in one call (BNBL v2). */
export function quoteExtraGuestSurchargePatterns(
  propertyId: string,
  roomTypeId: string,
  roomRateId: string,
  params: { currency: string, nights: number, offerType?: string },
): Promise<ExtraGuestSurchargePatternsQuote> {
  const query = new URLSearchParams({
    currency: params.currency,
    nights: String(params.nights),
  })
  if (params.offerType) {
    query.set('offer_type', params.offerType)
  }
  return request
    .get<App.ApiResponse<ExtraGuestSurchargePatternsQuote>>(
      `/api/properties/${propertyId}/room-types/${roomTypeId}/room-rates/${roomRateId}/extra-guest-surcharges/quote-patterns?${query.toString()}`,
      { credentials: 'include' },
    )
    .then(data => data.result)
}

function tourDateMap(date: any): App.TourDate {
  return {
    startDate: date.start_date,
    endDate: date.end_date,
    monthGroup: date.month_group,
    totalAvailable: date.total_available,
    guaranteedDeparture: date.guaranteedDeparture,
  }
}

export function tourEnquiry(
  tourId: string,
  tourOptionId: string,
  numberOfDays: number,
  noOfAdults: number,
) {
  return request.get<App.ApiResponse<Array<any>>>(templates.reservation.tour_enquiry.expand({
    tour_id: tourId,
    id: tourOptionId,
    days: numberOfDays,
    timezone_offset: timezoneOffset(),
    no_of_adults: noOfAdults,
  }))
    .then(data => data.result.map(tourDateMap))
}

function timezoneOffset() {
  return new Date().getTimezoneOffset() / 60 * -1
}
